import { type ReactNode, useState } from "react";
import { Download, ExternalLink, FileText, ImageDown, Loader2, Paperclip } from "lucide-react";

import { Toast } from "@/components/ui/Toast";

import { ArtifactBanner } from "@/components/chat/ArtifactBanner";
import { extractGoogleEmbeds } from "@/components/chat/googleEmbeds";
import { MarkdownContent } from "@/components/chat/MarkdownContent";
import { ResolverChoice } from "@/components/chat/ResolverChoice";
import { extractResolverOptions } from "@/components/chat/resolverOptions";
import { cn } from "@/lib/utils";
import { detectGoogleEmbed } from "@domain/google-embed";
import { parseAllMediaUploadBlocks } from "@domain/media-upload-block";

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const URL_RE = /(https?:\/\/[^\s<>)"']+)/g;
const INTEGRATION_LINK_RE = /\[INTEGRATION_LINK\]\s*(\{[\s\S]*?\})\s*\[\/INTEGRATION_LINK\]/g;

interface IntegrationLink {
  url?: string;
  provider?: string;
  label: string;
}

function extractIntegrationLinks(content: string): {
  cleanContent: string;
  links: IntegrationLink[];
} {
  const links: IntegrationLink[] = [];
  const cleanContent = content.replace(INTEGRATION_LINK_RE, (_match, json) => {
    try {
      const data = JSON.parse(json) as IntegrationLink;
      if (data.label && (data.url || data.provider)) links.push(data);
    } catch { /* skip malformed tags */ }
    return "";
  }).trim();
  return { cleanContent, links };
}

function linkify(text: string): ReactNode[] {
  const parts = text.split(URL_RE);
  return parts.map((part, i) =>
    URL_RE.test(part) ? (
      <a
        key={i}
        href={part}
        target="_blank"
        rel="noopener noreferrer"
        className="text-brand-coral underline underline-offset-2 hover:text-brand-coral/80"
      >
        {part}
      </a>
    ) : (
      part
    ),
  );
}

export interface MessageAttachment {
  filename: string;
  isGenerated?: boolean;
  mimeType: string;
  size?: number;
  url?: string;
}

interface MessageBubbleProps {
  role: string;
  content: string;
  kind?: "user" | "synthetic";
  pending?: boolean;
  isStreaming?: boolean;
  attachments?: MessageAttachment[];
}

export function MessageBubble({ role, content, kind, pending, isStreaming, attachments }: MessageBubbleProps) {
  const [downloadToast, setDownloadToast] = useState<string | null>(null);
  const isUser = role === "user";

  if (isUser && kind === "synthetic") {
    const { metas } = parseAllMediaUploadBlocks(content);
    if (metas.length > 0) {
      // Preview URL comes from the persisted `attachments` array (mapped to
      // .url via buildFilePreviewUrl(gcsPath) in useChat). Pair by filename
      // so order changes are safe; legacy rows missing an attachment fall
      // back to a non-link.
      const urlByName = new Map<string, string>();
      for (const att of attachments ?? []) {
        if (att.url) urlByName.set(att.filename, att.url);
      }
      return (
        <div className="flex justify-end">
          <div className="max-w-[85%] rounded-[14px_14px_4px_14px] border-2 border-dashed border-black/30 bg-white/40 p-4 text-sm text-black dark:border-white/30 dark:bg-white/5 dark:text-white">
            <div className="mb-2 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-black/60 dark:text-white/60">
              <Paperclip className="h-3.5 w-3.5" />
              <span>{`Uploaded ${metas.length} file${metas.length === 1 ? "" : "s"}`}</span>
            </div>
            <ul className="flex flex-col gap-1">
              {metas.map((m, i) => {
                const rowClass =
                  "flex items-center gap-2 rounded-md px-1 py-0.5 text-sm text-black dark:text-white";
                const rowBody = (
                  <>
                    <FileText className="h-3.5 w-3.5 shrink-0 opacity-70" />
                    <span className="truncate">{m.name}</span>
                    <span className="shrink-0 text-xs text-black/50 dark:text-white/50">
                      {formatFileSize(m.size)}
                    </span>
                  </>
                );
                const href = urlByName.get(m.name);
                return (
                  <li key={i}>
                    {href ? (
                      <a
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={`Open ${m.name} in a new tab`}
                        className={`${rowClass} no-underline hover:bg-black/5 hover:underline dark:hover:bg-white/10`}
                      >
                        {rowBody}
                      </a>
                    ) : (
                      <div className={rowClass}>{rowBody}</div>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      );
    }
  }
  const imageAttachments = attachments?.filter((a) => a.mimeType.startsWith("image/") && a.url) ?? [];
  const pdfAttachments = attachments?.filter((a) => a.mimeType === "application/pdf" && a.url) ?? [];
  const audioAttachments = attachments?.filter((a) => a.mimeType.startsWith("audio/") && a.url) ?? [];
  const { cleanContent: linkStripped, links } = isUser
    ? { cleanContent: content, links: [] }
    : extractIntegrationLinks(content);
  const { cleanContent: resolverStripped, options: resolverOptions } = isUser
    ? { cleanContent: linkStripped, options: null }
    : extractResolverOptions(linkStripped);
  // Google file links (Docs/Sheets/Slides/Drive) render as inline iframe
  // previews. The agent surfaces these as markdown links in the message body
  // (extracted here), and occasionally as [INTEGRATION_LINK] tags (handled
  // below); everything else (connect-app buttons, other URLs) stays a button.
  const { cleanContent, embeds: contentEmbeds } = isUser
    ? { cleanContent: resolverStripped, embeds: [] }
    : extractGoogleEmbeds(resolverStripped);
  const linkEmbeds = links
    .map((link) => ({ embed: link.url ? detectGoogleEmbed(link.url) : null, label: link.label }))
    .filter((e): e is { embed: NonNullable<typeof e.embed>; label: string } => e.embed !== null);
  const googleEmbeds = [...contentEmbeds, ...linkEmbeds];
  const buttonLinks = links.filter((link) => !(link.url && detectGoogleEmbed(link.url)));

  return (
    <div className={cn("flex", isUser ? "justify-end" : "justify-start")}>
      <div
        className={cn(
          "max-w-[85%] p-4 text-[15px] font-medium leading-relaxed",
          isUser
            ? "whitespace-pre-wrap rounded-[14px_14px_4px_14px] bg-[#1A1A1A] text-white"
            : "prose prose-base dark:prose-invert max-w-none rounded-[14px_14px_14px_4px] border border-black/5 bg-white/85 text-black shadow-sm backdrop-blur-sm dark:border-white/10 dark:bg-white/5 dark:backdrop-blur-xl dark:text-white",
          pending && "opacity-60",
        )}
      >
        {imageAttachments.length > 0 && (
          <div className={cn("flex flex-wrap gap-2", (cleanContent || pdfAttachments.length > 0 || audioAttachments.length > 0) && "mb-2")}>
            {imageAttachments.map((att, i) => (
              <div key={i} className="group relative">
                <a href={att.url} target="_blank" rel="noopener noreferrer">
                  <img
                    src={att.url}
                    alt={att.filename}
                    className="max-h-64 max-w-full rounded-lg object-contain"
                  />
                </a>
                {att.isGenerated && att.url && (
                  <button
                    type="button"
                    className="absolute bottom-2 right-2 flex h-8 w-8 items-center justify-center rounded-full bg-black/60 text-white opacity-0 transition-opacity group-hover:opacity-100"
                    title={`Download ${att.filename}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      const downloadUrl = `${att.url}&download=1`;
                      window.location.href = downloadUrl;
                      setDownloadToast(att.filename);
                      setTimeout(() => setDownloadToast(null), 2500);
                    }}
                  >
                    <Download className="h-4 w-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
        {pdfAttachments.length > 0 && (
          <div className={cn("flex flex-col", (cleanContent || audioAttachments.length > 0) && "mb-2")}>
            {pdfAttachments.map((att, i) => (
              <ArtifactBanner
                key={i}
                artifact={{ kind: "pdf", label: att.filename, url: att.url! }}
                sublabel={att.size != null ? `PDF · ${formatFileSize(att.size)}` : "PDF"}
              />
            ))}
          </div>
        )}
        {audioAttachments.length > 0 && (
          <div className={cn("flex flex-col gap-1.5", cleanContent && "mb-2")}>
            {audioAttachments.map((att, i) => (
              <div key={i} className="flex flex-col gap-1">
                <span className="text-xs text-neutral-400">{att.filename}</span>
                <audio controls src={att.url} className="max-w-full" />
              </div>
            ))}
          </div>
        )}
        {cleanContent && (isUser ? linkify(cleanContent) : <MarkdownContent content={cleanContent} />)}
        {googleEmbeds.map(({ embed, label }, i) => (
          <ArtifactBanner
            key={`embed-${i}`}
            artifact={{ kind: "google", label, embed }}
            sublabel="Google file · click to open"
          />
        ))}
        {buttonLinks.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {buttonLinks.map((link, i) => (
              <a
                key={i}
                href={link.url ?? `/integrations?connect=${encodeURIComponent(link.provider ?? "")}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-lg border border-brand-coral/30 bg-brand-coral/10 px-4 py-2 text-sm font-semibold text-brand-coral no-underline transition-colors hover:bg-brand-coral/20"
              >
                <ExternalLink className="h-4 w-4" />
                {link.label}
              </a>
            ))}
          </div>
        )}
        {resolverOptions !== null && <ResolverChoice options={resolverOptions} />}
        {isStreaming && <span className="animate-pulse">▍</span>}
        {pending && (
          <Loader2 className="ml-2 inline-block h-3 w-3 animate-spin text-brand-coral" />
        )}
      </div>
      <Toast
        icon={<ImageDown className="h-4 w-4 text-accent-green" />}
        message={`Downloaded ${downloadToast}`}
        isVisible={downloadToast !== null}
      />
    </div>
  );
}
